'use client'

import React from 'react'
import { cn } from '@kiao-voice/ui-kit'
import type { AnalyticsLocation } from '@/lib/analytics'
import { useSectionViewed } from '@/lib/analytics/use-section-viewed'

/**
 * 產品頁的區塊外殼與置中欄。設計稿用 `.section` + `.container` 兩個 class 表達
 * 同一件事；這裡拆成兩個元件，讓每個 section 元件只描述自己的內容。
 *
 * 分隔線刻意是 prop 而不是 `.section + .section` 的相鄰選擇器：hero 之後、
 * 墨底帶之後、外框收尾的區塊之後都不畫線，用相鄰選擇器表達要三條例外規則，
 * 用 prop 表達則是每個 page 檔自己看得見的一個字。
 */

/** 置中欄。手機側邊距 20px（32px 在 360–390px 寬的機器上吃掉快兩成版面）。 */
export function SiteContainer({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('mx-auto w-full max-w-(--container) px-5 min-[561px]:px-(--gutter)', className)}>
      {children}
    </div>
  )
}

/** 區塊底色：`paper`（預設淺底）或 `ink`（墨底反白）。 */
export type SiteSectionTone = 'paper' | 'ink'

export interface SiteSectionProps {
  id?: string
  /** 墨底反白區塊（隱私帶、文末 CTA）。 */
  tone?: SiteSectionTone
  /** 上緣髮絲線。相鄰兩個淺底區塊之間要，hero／墨底／外框收尾之後不要。 */
  divider?: boolean
  /**
   * Reports `site_section_viewed` with this as `location` the first time the
   * section is seen. The same kind of section uses the same key on every page.
   */
  trackingKey?: AnalyticsLocation
  className?: string
  children: React.ReactNode
}

export function SiteSection({
  id,
  tone = 'paper',
  divider = false,
  trackingKey,
  className,
  children,
}: SiteSectionProps) {
  const viewedRef = useSectionViewed(trackingKey)
  return (
    <section
      ref={viewedRef}
      id={id}
      className={cn(
        'py-(--section-space)',
        tone === 'ink' && 'bg-foreground text-background',
        divider && 'border-t border-border',
        className
      )}
    >
      <SiteContainer>{children}</SiteContainer>
    </section>
  )
}
